import { useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { cn } from "~/core/generic/shadcn-utils";

import { useSearchInputKeyDown } from "../../hooks/useSearchInputKeyDown";
import { useGlobalCommandbarStore } from "../../store";

export default function CommandbarSearchbar() {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const setSearchQuery = useGlobalCommandbarStore(st => st.setSearchQuery);
  const isOpenGlobalCommandbar = useGlobalCommandbarStore(
    st => st.isOpenGlobalCommandbar,
  );
  const setIsOpenGlobalCommandbar = useGlobalCommandbarStore(
    st => st.setIsOpenGlobalCommandbar,
  );
  const { onSearchInputKeyDown, onSearchInputKeyUp } = useSearchInputKeyDown();

  const [localSearchQuery, setLocalSearchQuery] = useState("");

  const debouncedSetSearchQuery = useDebouncedCallback(setSearchQuery, 300);

  useEffect(() => {
    if (!isOpenGlobalCommandbar) return;

    const searchInput = searchInputRef.current;

    setTimeout(() => {
      searchInput?.focus();
    }, 0);

    return () => {
      searchInput?.blur();
    };
  }, [isOpenGlobalCommandbar]);

  useEffect(() => {
    if (!searchInputRef.current) return;
    useGlobalCommandbarStore.setState({
      searchInputRef,
    });

    return () => {
      useGlobalCommandbarStore.setState({
        searchInputRef: null,
      });
    };
  }, [searchInputRef]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchQuery(e.target.value);
    debouncedSetSearchQuery(e.target.value);
  };

  const handleCloseGlobalCommandbar = () => {
    setIsOpenGlobalCommandbar(false);
    setSearchQuery("");
  };

  return (
    <div
      className={cn(
        "w-full rounded-base bg-sidebar text-sm text-darker",
        "focus:outline-none placeholder:text-lighter",
        "flex items-center justify-between gap-2 px-2",
      )}
    >
      <input
        className={cn(
          "w-full bg-transparent py-2 text-sm text-darker",
          "focus:outline-none placeholder:text-lighter",
        )}
        autoFocus
        ref={searchInputRef}
        placeholder="Search or command..."
        value={localSearchQuery}
        onChange={onInputChange}
        onKeyDown={onSearchInputKeyDown}
        onKeyUp={onSearchInputKeyUp}
      />

      <kbd
        className="text-light text-xs hover:text-darker cursor-pointer transition-colors duration-300 ease-linear"
        onClick={handleCloseGlobalCommandbar}
      >
        Esc
      </kbd>
    </div>
  );
}
